/**
 * Profitability and supplier performance views (batch 8 T14c, #3036).
 *
 * Profitability is financial-only: cost is the FIFO layer cost known today, revenue is the frozen invoice line. A
 * margin that cannot be measured says why and is never 0; legacy movements that carry no booking, line or cost are
 * shown apart. Supplier performance keeps each metric's own sample: a small sample is not ranked, unobserved
 * quality is not a zero rejection and a short-closed order is not a delivery.
 */
import type { ReactNode } from 'react';
import { Chip, Stack, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { WireMetric, WireProfitability, WireSampled, WireSupplierPerformance } from '@/hooks/useInventoryReports';
import { Cents, Figure, MetricValue, Money, Named, PartialWarning, ReconciledTotal, ReportTable, SourceNote, Stat, Unknown, WindowInfo, useReportDisplay } from './ReportParts';
import { formatPercent } from './reportFormat';
import { profitKindKey } from './reportLabels';

export function ProfitabilityView({ data }: { data: WireProfitability }) {
  const { t } = useTranslation('inventoryReports');
  const { language } = useReportDisplay();
  const s = data.summary;
  return (
    <Stack spacing={2}>
      <WindowInfo window={data.window} />
      <SourceNote source={data.source} />
      <Typography variant="caption" color="text.secondary">
        {t('profit.revenueBasis')}
      </Typography>
      {s.unknownCostUnits > 0 && (
        <PartialWarning>{t('profit.partial', { units: s.unknownCostUnits, events: s.unknownCostEvents })}</PartialWarning>
      )}
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
        <Stat label={t('profit.facts')}>
          <Figure value={s.facts} />
        </Stat>
        <ReconciledTotal label={t('profit.knownCost')} total={s.totalKnownCost} />
        <ReconciledTotal label={t('profit.frozenRevenue')} total={s.frozenRevenue} />
        <Stat
          label={t('profit.knownMargin')}
          caption={s.knownMarginPercent === null ? t('profit.percentUnknown') : formatPercent(s.knownMarginPercent, language)}
        >
          <Money cents4={s.knownMarginCents4} cents={s.knownMarginCents} />
        </Stat>
        <Stat label={t('profit.unknownCostRevenue')}>
          <Money cents4={s.unknownCostRevenueCents4} />
        </Stat>
        <Stat label={t('profit.costWithoutRevenue')}>
          <Money cents4={s.costWithoutRevenueCents4} />
        </Stat>
      </Stack>
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
        <Stat label={t('profit.orphan')} caption={t('profit.bucketCaption', { units: s.orphan.units, unknown: s.orphan.unknownCostUnits })}>
          <Money cents4={s.orphan.knownCostCents4} />
        </Stat>
        <Stat label={t('profit.directSale')} caption={t('profit.bucketCaption', { units: s.directSale.units, unknown: s.directSale.unknownCostUnits })}>
          <Money cents4={s.directSale.knownCostCents4} />
        </Stat>
        <Stat label={t('profit.legacyUnits')} caption={t('profit.legacyNote')}>
          <MetricValue metric={data.unattributedLegacyUnits} render={(v) => <Figure value={v} />} />
        </Stat>
        <Stat label={t('profit.legacyEvents')}>
          <MetricValue metric={data.unattributedLegacyEvents} render={(v) => <Figure value={v} />} />
        </Stat>
      </Stack>
      <ReportTable label={t('profit.label')}>
        <TableHead>
          <TableRow>
            <TableCell>{t(`profit.group.${data.groupBy}`)}</TableCell>
            <TableCell>{t('profit.kind')}</TableCell>
            <TableCell align="right">{t('profit.units')}</TableCell>
            <TableCell align="right">{t('profit.knownCost')}</TableCell>
            <TableCell align="right">{t('profit.revenue')}</TableCell>
            <TableCell align="right">{t('profit.margin')}</TableCell>
            <TableCell align="right">{t('profit.knownMargin')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.rows.map((row) => (
            <TableRow key={`${row.groupKey ?? 'none'}|${row.kind}`}>
              <TableCell>
                <Named kind={data.groupBy} id={row.groupKey} sku={row.sku} />
              </TableCell>
              <TableCell><Chip size="small" variant="outlined" label={t(profitKindKey(row.kind))} /></TableCell>
              <TableCell align="right">
                <Figure value={row.units} />
                {row.unknownCostUnits > 0 && (
                  <Typography variant="caption" color="warning.main" component="div">
                    {t('profit.unknownUnits', { count: row.unknownCostUnits })}
                  </Typography>
                )}
                {row.confirmedAfterConsumptionUnits > 0 && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    {t('profit.confirmedLate', { count: row.confirmedAfterConsumptionUnits })}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right"><Money cents4={row.knownCostCents4} cents={row.knownCostCents} /></TableCell>
              <TableCell align="right"><MetricValue metric={row.revenue} render={(v) => <Money cents4={v.cents4} cents={v.cents} />} /></TableCell>
              <TableCell align="right">
                <MetricValue
                  metric={row.margin}
                  render={(v) => (
                    <>
                      <Money cents4={v.cents4} cents={v.cents} />
                      <Typography variant="caption" color="text.secondary" component="div">
                        {v.percent === null ? t('profit.percentUnknown') : formatPercent(v.percent, language)}
                      </Typography>
                    </>
                  )}
                />
              </TableCell>
              <TableCell align="right"><Money cents4={row.knownMarginCents4} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </ReportTable>
    </Stack>
  );
}

/** One sampled metric: its value, the size of the sample, and whether the sample is too small to rank. */
function Sampled<T>({ sampled, render, extra }: { sampled: WireSampled<T>; render: (value: T) => ReactNode; extra?: ReactNode }) {
  const { t } = useTranslation('inventoryReports');
  return (
    <>
      <MetricValue metric={sampled.metric as WireMetric<T>} render={render} />
      <Typography variant="caption" color="text.secondary" component="div">
        {t('supplier.sample', { count: sampled.sampleSize })}
      </Typography>
      {sampled.insufficientSample && (
        <Chip size="small" color="warning" variant="outlined" label={t('supplier.insufficient')} sx={{ mr: 0.5 }} />
      )}
      {!sampled.rankable && <Chip size="small" variant="outlined" label={t('supplier.notRankable')} />}
      {extra}
    </>
  );
}

const Note = ({ children }: { children: ReactNode }) => (
  <Typography variant="caption" color="text.secondary" component="div">
    {children}
  </Typography>
);

export function SupplierPerformanceView({ data }: { data: WireSupplierPerformance }) {
  const { t } = useTranslation('inventoryReports');
  const { language } = useReportDisplay();
  const percent = (value: number | null) => (value === null ? t('supplier.noPercent') : formatPercent(value, language));
  return (
    <Stack spacing={2}>
      <WindowInfo window={data.window} />
      <Typography variant="body2" color="text.secondary">
        {t('supplier.minimumSample', { count: data.minimumSample })}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {t('supplier.notes')}
      </Typography>
      <ReportTable label={t('supplier.label')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('supplier.supplier')}</TableCell>
            <TableCell align="right">{t('supplier.orders')}</TableCell>
            <TableCell>{t('supplier.onTime')}</TableCell>
            <TableCell>{t('supplier.fillRate')}</TableCell>
            <TableCell>{t('supplier.rejection')}</TableCell>
            <TableCell>{t('supplier.leadTime')}</TableCell>
            <TableCell>{t('supplier.invoiceAccuracy')}</TableCell>
            {data.financial && <TableCell align="right">{t('supplier.priceVariance')}</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.suppliers.map((s) => (
            <TableRow key={s.supplierId} sx={{ verticalAlign: 'top' }}>
              <TableCell>{s.supplierName}</TableCell>
              <TableCell align="right">
                <Figure value={s.orders} />
                <Note>
                  {t('supplier.outcomes', {
                    complete: s.outcomes.complete,
                    shortClosed: s.outcomes.complete_short_closed,
                    held: s.outcomes.held_unresolved,
                    incomplete: s.outcomes.incomplete,
                  })}
                </Note>
              </TableCell>
              <TableCell>
                <Sampled
                  sampled={s.onTime}
                  render={(v) => (
                    <>
                      {percent(v.percent)} <Note>{t('supplier.ratio', { part: v.onTime, whole: v.eligible })}</Note>
                    </>
                  )}
                  extra={
                    <Note>
                      {t('supplier.onTimeNotes', { noDate: s.onTime.noExpectedDate, noTime: s.onTime.completionTimeUnavailable, shortClosed: s.onTime.shortClosedExcluded })}
                    </Note>
                  }
                />
              </TableCell>
              <TableCell>
                <Sampled
                  sampled={s.fillRate}
                  render={(v) => (
                    <>
                      {percent(v.percent)} <Note>{t('supplier.ratio', { part: v.filledUnits, whole: v.expectedUnits })}</Note>
                    </>
                  )}
                  extra={s.fillRate.overDeliveredUnits > 0 ? <Note>{t('supplier.overDelivered', { count: s.fillRate.overDeliveredUnits })}</Note> : undefined}
                />
              </TableCell>
              <TableCell>
                <Sampled
                  sampled={s.rejectionRate}
                  render={(v) => (
                    <>
                      {percent(v.percent)} <Note>{t('supplier.ratio', { part: v.rejectedUnits, whole: v.evaluatedUnits })}</Note>
                      <Note>
                        {t('supplier.coverage', {
                          percent: v.observedCoveragePercent === null ? t('supplier.noPercent') : formatPercent(v.observedCoveragePercent, language),
                          observed: v.observedLines,
                          unobserved: v.unobservedLines,
                        })}
                      </Note>
                    </>
                  )}
                  extra={
                    s.rejectionRate.metric.available && s.rejectionRate.metric.value.unobservedLines > 0 ? (
                      <Note>{t('supplier.unobserved')}</Note>
                    ) : undefined
                  }
                />
              </TableCell>
              <TableCell>
                <Sampled
                  sampled={s.leadTime}
                  render={(v) => (
                    <>
                      {v.medianDays === null ? t('supplier.noPercent') : t('supplier.days', { count: v.medianDays })}
                      <Note>{t('supplier.leadSources', { dispatch: v.fromDispatch, order: v.fromOrderDate })}</Note>
                    </>
                  )}
                  extra={<Note>{t('supplier.leadNotes', { none: s.leadTime.noReceiptEvidence, negative: s.leadTime.negativeInterval })}</Note>}
                />
              </TableCell>
              <TableCell>
                <Sampled
                  sampled={s.invoiceAccuracy}
                  render={(v) => (
                    <>
                      {percent(v.percent)} <Note>{t('supplier.ratio', { part: v.matched, whole: v.evaluated })}</Note>
                    </>
                  )}
                  extra={
                    <Note>
                      {t('supplier.invoiceNotes', {
                        variance: s.invoiceAccuracy.acceptedVariance,
                        rejected: s.invoiceAccuracy.rejected,
                        draft: s.invoiceAccuracy.draft,
                        nonEur: s.invoiceAccuracy.nonEur,
                        legacy: s.invoiceAccuracy.legacyHeaderInvoiceOrders,
                      })}
                    </Note>
                  }
                />
              </TableCell>
              {data.financial && (
                <TableCell align="right">
                  {s.priceVariance === undefined ? (
                    <Unknown reason="no_history" />
                  ) : (
                    <>
                      <Cents cents={s.priceVariance.openCents} />
                      <Note>{t('supplier.openLines', { count: s.priceVariance.openLines })}</Note>
                      <Cents cents={s.priceVariance.acceptedCents} />
                      <Note>{t('supplier.acceptedLines', { count: s.priceVariance.acceptedLines })}</Note>
                    </>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </ReportTable>
    </Stack>
  );
}
